import PropTypes from 'prop-types';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import brand from 'utils/designTokens';

const countChipSx = {
  height: 24,
  fontWeight: 700,
  fontSize: '0.75rem',
  bgcolor: 'rgba(31, 91, 64, 0.08)',
  color: 'text.primary',
  border: `1px solid ${brand.border}`
};

const nestedCountChipSx = {
  height: 24,
  minWidth: 28,
  fontWeight: 600,
  fontSize: '0.75rem',
  bgcolor: '#f3f4f6',
  color: '#6b7280',
  border: 'none',
  '& .MuiChip-label': { px: 1 }
};

const primaryButtonSx = {
  width: { xs: '100%', sm: 'auto' },
  px: 2.5,
  py: 1.1,
  borderRadius: 2.5,
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 14px rgba(31, 91, 64, 0.22)',
  '&:hover': {
    boxShadow: '0 6px 20px rgba(31, 91, 64, 0.28)'
  }
};

const nestedPrimaryButtonSx = {
  ...primaryButtonSx,
  borderRadius: '999px',
  py: 1,
  px: 2.25,
  fontWeight: 700,
  boxShadow: '0 4px 14px rgba(31, 91, 64, 0.2)'
};

/** Standard back control for drill-down / view screens */
export function PageHeaderBackButton({ onClick, title = 'Back' }) {
  return (
    <Tooltip title={title} arrow>
      <IconButton
        size="small"
        onClick={onClick}
        aria-label={title}
        sx={{
          mt: 0.15,
          color: 'text.secondary',
          '&:hover': { bgcolor: 'rgba(31, 91, 64, 0.06)', color: 'text.primary' }
        }}
      >
        <ArrowBackIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

PageHeaderBackButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  title: PropTypes.string
};

// ==============================|| PAGE HEADER ||============================== //

export default function PageHeader({
  title,
  subtitle,
  count,
  variant = 'default',
  onBack,
  backTitle = 'Back',
  leading,
  titleExtra,
  action,
  actions,
  actionLabel,
  actionIcon: ActionIcon,
  onAction,
  actionDisabled = false,
  children
}) {
  const isNested = variant === 'nested';

  const resolvedLeading =
    leading ?? (onBack ? <PageHeaderBackButton onClick={onBack} title={backTitle} /> : null);

  const rightActions =
    actions ??
    action ??
    (actionLabel && onAction ? (
      <Button
        variant="contained"
        color="primary"
        onClick={onAction}
        disabled={actionDisabled}
        startIcon={ActionIcon ? <ActionIcon /> : undefined}
        sx={isNested ? nestedPrimaryButtonSx : primaryButtonSx}
      >
        {actionLabel}
      </Button>
    ) : null);

  return (
    <Box
      sx={{
        mb: 3,
        p: { xs: 2, sm: 2.5 },
        borderRadius: `${brand.radius?.lg || 16}px`,
        background: isNested ? '#ffffff' : 'linear-gradient(135deg, #ffffff 0%, #f8fbf9 100%)',
        border: isNested ? 'none' : `1px solid ${brand.border}`,
        boxShadow: isNested ? brand.shadowMd : brand.shadowSm,
        animation: 'fadeInUp 0.35s ease-out both'
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ minWidth: 0, flex: 1 }}>
          {resolvedLeading}
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                  fontSize: { xs: '1.25rem', sm: isNested ? '1.5rem' : '1.375rem' }
                }}
              >
                {title}
              </Typography>
              {count != null && (
                <Chip label={count} size="small" sx={isNested ? nestedCountChipSx : countChipSx} />
              )}
              {titleExtra}
            </Stack>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>

        {rightActions && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
          >
            {rightActions}
          </Stack>
        )}
      </Stack>
      {children}
    </Box>
  );
}

PageHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  variant: PropTypes.oneOf(['default', 'nested']),
  onBack: PropTypes.func,
  backTitle: PropTypes.string,
  leading: PropTypes.node,
  titleExtra: PropTypes.node,
  action: PropTypes.node,
  actions: PropTypes.node,
  actionLabel: PropTypes.string,
  actionIcon: PropTypes.elementType,
  onAction: PropTypes.func,
  actionDisabled: PropTypes.bool,
  children: PropTypes.node
};
